import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

import { formatDateDayMonthYear } from '../../../utils/formatters';
import DateSelect from '../DateSelect';
import TrendChart, { ChartItems } from '../TrendChart';

import SpinnerGrowLoading from './SpinnerGrowLoading';
import { dusk } from 'utils/helpers';

export type GetTrendResponse = {
    value: string | number;
    trend: Array<{ date: string; value: number }>;
};

type Props = {
    url: string;
    label: string;
    range: string | null;
    startDate: Moment | null;
    endDate: Moment | null;
    elementId: string;
};

function TrendMetric({ url, label, range, startDate, endDate, elementId }: Props) {
    const [startDay, setStartDay] = useState<Moment>(startDate);
    const [endDay, setEndDay] = useState<Moment>(endDate);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [chartData, setChartData] = useState<ChartItems>([]);
    const [fetchedData, setFetchedData] = useState<GetTrendResponse>({ value: 0, trend: [] });

    useEffect(() => {
        if (!startDay || !Array.isArray(fetchedData.trend)) {
            return;
        }

        if (!startDay.isValid() && fetchedData.trend.length > 1) {
            // all-time filter applied with more than one trend item
            const fetchedStartDay = moment(fetchedData.trend[0].date).startOf('day');
            const fetchedEndDay = moment(fetchedData.trend[fetchedData.trend.length - 1].date).endOf('day');
            setChartData(getChartData(fetchedStartDay, fetchedEndDay, fetchedData));

            return;
        }

        setChartData(getChartData(startDay, endDay, fetchedData));
    }, [fetchedData]);

    return (
        <div className="card">
            <div className="card-body position-relative">
                <div className="d-flex">
                    <div>
                        <span className="text-muted">{label}</span>
                        <SpinnerGrowLoading isLoading={isLoading}>
                            <h3 className="font-light m-b-5" {...dusk(label.replace(/ /g, '_').toLowerCase())}>{fetchedData.value ?? 0}</h3>
                        </SpinnerGrowLoading>
                    </div>
                    <div className="ml-auto metrics_datepicker">
                        <DateSelect
                            url={url}
                            setData={setFetchedData}
                            defaultRange={range}
                            defaultStartDate={startDate}
                            defaultEndDate={endDate}
                            setStartDay={setStartDay}
                            setEndDay={setEndDay}
                            setIsLoading={setIsLoading}
                        />
                    </div>
                </div>
                <TrendChart dataset={chartData} elementId={elementId} isLoading={isLoading} />
            </div>
        </div>
    );
}

function getChartData(startDay: Moment, endDay: Moment, fetchedData: GetTrendResponse): ChartItems {
    let differenceOfDays = moment(endDay).diff(moment(startDay), 'days') || 0;

    if (!differenceOfDays) {
        return []; // we don't want to draw a chart for one day
    }

    const pointer = startDay.clone();
    const dateRange = [pointer.clone()];

    while (differenceOfDays--) {
        dateRange.push(pointer.add(1, 'days').clone());
    }

    return dateRange.map((date) => {
        const trend = fetchedData.trend.find((item) => date.isSame(moment(item.date), 'day'));

        return trend ? { meta: formatDateDayMonthYear(trend.date, 'en'), value: trend.value } : 0;
    });
}

export default TrendMetric;
